/**
 * TBC Product Media Gallery
 * Desktop: CSS grid (2/1/2)
 * Mobile (≤750px): Swiper main + thumbs
 */
 
class TBCProductMediaGallery {
  constructor(root) {
    this.root = root;
    this.mainEl = root.querySelector('[data-tbc-main-slider]');
    this.thumbsEl = root.querySelector('[data-tbc-thumbs-slider]');
    this.prevEl = root.querySelector('[data-tbc-slider-prev]');
    this.nextEl = root.querySelector('[data-tbc-slider-next]');
    this.paginationEl = root.querySelector('[data-tbc-slider-pagination]');
    this.mediaCount = Number(root.dataset.mediaCount || 0);
    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.mq = window.matchMedia('(max-width: 750px)');
    this.onMqChange = this.onMqChange.bind(this);
 
    if (!this.mainEl || this.mediaCount < 1) return;
 
    this.init();
  }
 
  init() {
    if (this.mq.matches) {
      this.mount();
    }
 
    if (typeof this.mq.addEventListener === 'function') {
      this.mq.addEventListener('change', this.onMqChange);
    } else if (typeof this.mq.addListener === 'function') {
      this.mq.addListener(this.onMqChange);
    }
  }
 
  onMqChange(event) {
    if (event.matches) {
      this.mount();
    } else {
      this.destroy();
    }
  }
 
  mount() {
    if (this.mainSwiper || typeof Swiper === 'undefined') return;
 
    if (this.thumbsEl && this.mediaCount > 1) {
      this.thumbsSwiper = new Swiper(this.thumbsEl, {
        slidesPerView: 'auto',
        spaceBetween: 4,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        freeMode: true,
      });
    }
 
    const mainOptions = {
      loop: false,
      speed: 400,
      slidesPerView: 1,
      spaceBetween: 0,
      allowTouchMove: true,
      watchOverflow: true,
      on: {
        init: (swiper) => {
          this.updateNavVisibility(swiper);
          this.syncSlideMedia(swiper);
        },
        slideChange: (swiper) => {
          this.updateNavVisibility(swiper);
          this.pauseAllMedia(swiper);
        },
        slideChangeTransitionEnd: (swiper) => {
          this.syncSlideMedia(swiper);
        },
      },
    };
 
    if (this.mediaCount > 1) {
      mainOptions.navigation = {
        prevEl: this.prevEl,
        nextEl: this.nextEl,
      };
 
      mainOptions.pagination = {
        el: this.paginationEl,
        clickable: true,
        renderBullet: (index, className) => {
          return `<button type="button" class="${className}" aria-label="Go to image ${index + 1}"></button>`;
        },
      };
    }
 
    if (this.thumbsSwiper) {
      mainOptions.thumbs = {
        swiper: this.thumbsSwiper,
      };
    }
 
    this.mainSwiper = new Swiper(this.mainEl, mainOptions);
  }
 
  updateNavVisibility(swiper) {
    if (!this.prevEl || !this.nextEl) return;
 
    const isBeginning = swiper.isBeginning;
    const isEnd = swiper.isEnd;
 
    this.prevEl.hidden = isBeginning;
    this.nextEl.hidden = isEnd;
    this.prevEl.setAttribute('aria-disabled', String(isBeginning));
    this.nextEl.setAttribute('aria-disabled', String(isEnd));
  }
 
  pauseAllMedia(swiper) {
    const slides = swiper.slides || [];
 
    slides.forEach((slide) => {
      this.pauseSlideMedia(slide);
    });
  }
 
  syncSlideMedia(swiper) {
    const slides = swiper.slides || [];
 
    slides.forEach((slide, index) => {
      if (index === swiper.activeIndex) {
        this.playSlideMedia(slide);
      } else {
        this.pauseSlideMedia(slide);
      }
    });
  }
 
  playSlideMedia(slide) {
    if (!slide) return;
 
    const video = slide.querySelector('video');
    const iframe = slide.querySelector('iframe');
 
    if (video) {
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
 
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
 
    if (iframe) {
      this.setIframePlayback(iframe, true);
    }
  }
 
  pauseSlideMedia(slide) {
    if (!slide) return;
 
    const video = slide.querySelector('video');
    const iframe = slide.querySelector('iframe');
 
    if (video) {
      video.pause();
    }
 
    if (iframe) {
      this.setIframePlayback(iframe, false);
    }
  }
 
  setIframePlayback(iframe, shouldPlay) {
    const originalSrc = iframe.dataset.tbcSrc || iframe.getAttribute('src');
    if (!originalSrc) return;
 
    if (!iframe.dataset.tbcSrc) {
      iframe.dataset.tbcSrc = originalSrc;
    }
 
    let nextSrc = iframe.dataset.tbcSrc;
 
    try {
      const url = new URL(nextSrc, window.location.href);
 
      if (shouldPlay) {
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('mute', '1');
        url.searchParams.set('muted', '1');
        url.searchParams.set('playsinline', '1');
        url.searchParams.set('enablejsapi', '1');
      } else {
        url.searchParams.set('autoplay', '0');
        url.searchParams.delete('mute');
        url.searchParams.delete('muted');
      }
 
      nextSrc = url.toString();
    } catch (error) {
      // Keep original src if URL parsing fails
    }
 
    if (iframe.getAttribute('src') !== nextSrc) {
      iframe.setAttribute('src', nextSrc);
    }
 
    if (!shouldPlay) {
      try {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
          '*'
        );
        iframe.contentWindow?.postMessage(
          JSON.stringify({ method: 'pause' }),
          '*'
        );
      } catch (error) {
        // Ignore cross-origin postMessage errors
      }
    }
  }
 
  destroy() {
    if (this.mainSwiper) {
      this.pauseAllMedia(this.mainSwiper);
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
 
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
 
    if (this.prevEl) this.prevEl.hidden = false;
    if (this.nextEl) this.nextEl.hidden = false;
  }
}
 
function whenSwiperReady(callback) {
  if (typeof Swiper !== 'undefined') {
    callback();
    return;
  }
 
  let attempts = 0;
  const maxAttempts = 40;
  const timer = setInterval(() => {
    attempts += 1;
 
    if (typeof Swiper !== 'undefined') {
      clearInterval(timer);
      callback();
      return;
    }
 
    if (attempts >= maxAttempts) {
      clearInterval(timer);
    }
  }, 50);
}
 
function initTBCProductMediaGalleries() {
  whenSwiperReady(() => {
    document.querySelectorAll('.tbc-product-media-gallery').forEach((root) => {
      if (root.dataset.tbcGalleryInit === 'true') return;
      root.dataset.tbcGalleryInit = 'true';
      new TBCProductMediaGallery(root);
    });
  });
}
 
document.addEventListener('DOMContentLoaded', initTBCProductMediaGalleries);
 
document.addEventListener('shopify:section:load', (event) => {
  const gallery = event.target.querySelector('.tbc-product-media-gallery');
  if (!gallery) return;
 
  whenSwiperReady(() => {
    gallery.dataset.tbcGalleryInit = 'false';
    new TBCProductMediaGallery(gallery);
  });
});
 
/**
 * TBC Product Info — variants, ATC, accordions, complete the look
 */
class TBCProductInfo {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-tbc-product-form]');
    this.variantInput = root.querySelector('[data-tbc-variant-id]');
    this.priceCurrent = root.querySelector('[data-tbc-price-current]');
    this.priceCompare = root.querySelector('[data-tbc-price-compare]');
    this.priceBadge = root.querySelector('[data-tbc-price-badge]');
    this.readyToShip = root.querySelector('[data-tbc-ready-to-ship]');
    this.atcButton = root.querySelector('[data-tbc-atc]');
    this.atcText = root.querySelector('[data-tbc-atc-text]');
    this.optionNodes = [...root.querySelectorAll('[data-tbc-option]')];
    this.readyThreshold = Number(root.dataset.readyThreshold || 10);
    this.updateUrl = root.dataset.updateUrl === 'true';
    this.productUrl = root.dataset.url || '';
    this.addToCartLabel = root.dataset.atcLabel || 'ADD TO CART';
    this.soldOutLabel = root.dataset.soldOutLabel || 'SOLD OUT';
    this.product = this.parseJSON(root.querySelector('[data-tbc-product-json]'));
    this.currentVariant = null;
 
    if (!this.product) return;
 
    this.selectedOptions = this.product.options.map((_, index) => {
      const selectedBtn = this.root.querySelector(
        `[data-tbc-option][data-option-index="${index}"] [data-tbc-option-btn].is-selected`
      );
      return selectedBtn ? selectedBtn.dataset.value : this.product.variants[0]?.[`option${index + 1}`];
    });
 
    this.bindEvents();
    this.updateVariant();
    this.initAccordions();
    this.initCompleteTheLook();
  }
 
  parseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }
 
  bindEvents() {
    this.root.addEventListener('click', (event) => {
      const optionBtn = event.target.closest('[data-tbc-option-btn]');
      if (!optionBtn || !this.root.contains(optionBtn)) return;
      if (optionBtn.classList.contains('is-disabled') || optionBtn.getAttribute('aria-disabled') === 'true') {
        return;
      }
 
      const optionEl = optionBtn.closest('[data-tbc-option]');
      if (!optionEl) return;
 
      const optionIndex = Number(optionEl.dataset.optionIndex);
      this.selectedOptions[optionIndex] = optionBtn.dataset.value;
 
      optionEl.querySelectorAll('[data-tbc-option-btn]').forEach((btn) => {
        const selected = btn === optionBtn;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-pressed', String(selected));
      });
 
      const labelValue = optionEl.querySelector('[data-tbc-option-value]');
      if (labelValue) labelValue.textContent = optionBtn.dataset.value;
 
      this.updateVariant();
    });
 
    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.addToCart(this.form, this.atcButton);
      });
    }
  }
 
  getVariantFromOptions() {
    return this.product.variants.find((variant) =>
      this.selectedOptions.every((value, index) => variant[`option${index + 1}`] === value)
    ) || null;
  }
 
  updateVariant() {
    this.currentVariant = this.getVariantFromOptions();
    this.updateOptionAvailability();
 
    if (!this.currentVariant) {
      if (this.atcButton) this.atcButton.disabled = true;
      if (this.atcText) this.atcText.textContent = this.soldOutLabel;
      //if (this.readyToShip) this.readyToShip.classList.add('is-hidden');
      return;
    }
 
    if (this.variantInput) this.variantInput.value = this.currentVariant.id;
    this.updatePrice(this.currentVariant);
    this.updateReadyToShip(this.currentVariant);
    this.updateAddToCart(this.currentVariant);
    this.updateUrlState(this.currentVariant);
  }
 
  updateOptionAvailability() {
    this.optionNodes.forEach((optionEl) => {
      const optionIndex = Number(optionEl.dataset.optionIndex);
 
      optionEl.querySelectorAll('[data-tbc-option-btn]').forEach((btn) => {
        const trialOptions = [...this.selectedOptions];
        trialOptions[optionIndex] = btn.dataset.value;
 
        const hasAvailable = this.product.variants.some((variant) => {
          const matches = trialOptions.every((value, index) => {
            if (!value) return true;
            return variant[`option${index + 1}`] === value;
          });
          return matches && variant.available;
        });
 
        btn.classList.toggle('is-disabled', !hasAvailable);
        btn.setAttribute('aria-disabled', String(!hasAvailable));
      });
    });
  }
 
  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const currency = window.theme?.shop?.currency;

    if (currency) {
      try {
        return new Intl.NumberFormat(window.theme?.shop?.locale || undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'symbol',
        }).format(amount);
      } catch (error) {
        // Fall through to symbol + amount fallback.
      }
    }

    const symbol = window.theme?.shop?.currencySymbol || '';
    const formattedAmount = amount.toLocaleString(window.theme?.shop?.locale || undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${symbol}${formattedAmount}`;
  }
 
  updatePrice(variant) {
    if (this.priceCurrent) {
      this.priceCurrent.textContent = this.formatMoney(variant.price);
    }
 
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
 
    if (this.priceCompare) {
      this.priceCompare.textContent = onSale ? this.formatMoney(variant.compare_at_price) : '';
      this.priceCompare.classList.toggle('is-hidden', !onSale);
    }
 
    if (this.priceBadge) {
      if (onSale) {
        const percent = Math.round(
          ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
        );
        this.priceBadge.textContent = `-${percent}%`;
        this.priceBadge.classList.remove('is-hidden');
      } else {
        this.priceBadge.classList.add('is-hidden');
      }
    }
  }
 
  updateReadyToShip(variant) {
    if (!this.readyToShip) return;
 
    const inventory = Number(variant.inventory_quantity || 0);
    const show = Boolean(variant.available && inventory > this.readyThreshold);
    //this.readyToShip.classList.toggle('is-hidden', !show);
  }
 
  updateAddToCart(variant) {
    if (!this.atcButton || !this.atcText) return;
 
    if (variant.available) {
      this.atcButton.disabled = false;
      this.atcText.textContent = this.addToCartLabel;
    } else {
      this.atcButton.disabled = true;
      this.atcText.textContent = this.soldOutLabel;
    }
  }
 
  updateUrlState(variant) {
    if (!this.updateUrl || !history?.replaceState) return;
 
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }
 
  setButtonLabel(button, label, useHtml = false) {
    const textEl = button.querySelector('[data-tbc-atc-text]');
    if (textEl) {
      textEl.textContent = label;
      return;
    }
 
    if (useHtml) {
      button.innerHTML = label;
    } else {
      button.textContent = label;
    }
  }
 
  getCartApi() {
    return window.theme?.partials?.Cart || null;
  }
 
  getJQuery() {
    return window.jQuery || window.$ || null;
  }
 
  buildCartAddForm(form) {
    const $ = this.getJQuery();
    if (!$ || !form) return null;
 
    const variantInput =
      form.querySelector('[name="id"]') ||
      form.querySelector('[data-tbc-variant-id]') ||
      form.querySelector('[data-tbc-ctl-variant-id]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const variantId = variantInput?.value;
    if (!variantId) return null;
 
    const cartForm = $('<form></form>');
    cartForm.append(
      $('<input>', {
        type: 'hidden',
        name: 'id',
        value: variantId,
      })
    );
    cartForm.append(
      $('<input>', {
        type: 'hidden',
        name: 'quantity',
        value: quantityInput?.value || '1',
      })
    );
 
    return cartForm;
  }
 
  openCartDrawer() {
    const $ = this.getJQuery();
    const offCanvas = window.theme?.partials?.OffCanvas;
 
    // Prefer the theme OffCanvas API so we open instead of toggling closed.
    if (offCanvas) {
      offCanvas.right_sidebar_view = 'cart';
      if (offCanvas.right_sidebar?.length) {
        offCanvas.right_sidebar.attr('data-active', 'cart');
      }
 
      if (offCanvas.state === 'closed' && typeof offCanvas.openRight === 'function') {
        offCanvas.openRight();
        return;
      }
 
      if (offCanvas.state === 'right-open') {
        return;
      }
    }
 
    if (!$) return;
 
    const cartTrigger = $('[data-off-canvas--open="right-sidebar"][data-off-canvas--view="cart"]').first();
    if (cartTrigger.length) {
      cartTrigger.trigger('click');
      return;
    }
 
    $('[data-off-canvas--open="right-sidebar"]').first().trigger('click');
  }
 
  refreshAndOpenCart() {
    return new Promise((resolve) => {
      const cart = this.getCartApi();
 
      const finish = () => {
        this.openCartDrawer();
        resolve(true);
      };
 
      if (!cart || typeof cart.updateAllHtml !== 'function') {
        document.dispatchEvent(new CustomEvent('cart:updated'));
        finish();
        return;
      }
 
      if (typeof cart.updateAllHasItems === 'function') {
        cart.updateAllHasItems(1, true);
      }
      if (typeof cart.updateTotals === 'function') {
        cart.updateTotals();
      }
 
      cart.updateAllHtml(finish);
    });
  }
 
  addItemWithThemeCart(form) {
    return new Promise((resolve, reject) => {
      const cart = this.getCartApi();
      const $ = this.getJQuery();
      const cartForm = this.buildCartAddForm(form);
 
      if (!cart || typeof cart.addItem !== 'function' || !$ || !cartForm) {
        resolve(false);
        return;
      }
 
      cart.addItem(cartForm, (success, error) => {
        if (!success) {
          reject(new Error(error || 'Add to cart failed'));
          return;
        }
 
        this.refreshAndOpenCart().then(() => resolve(true));
      });
    });
  }
 
  async addItemWithFetch(form) {
    const formData = new FormData(form);
    if (!formData.get('quantity')) {
      formData.set('quantity', '1');
    }
 
    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });
 
    if (!response.ok) throw new Error('Add to cart failed');
 
    await response.json();
    await this.refreshAndOpenCart();
  }
 
  async addToCart(form, button) {
    if (!form || !button || button.disabled) return;
 
    const textEl = button.querySelector('[data-tbc-atc-text]');
    const previousLabel = textEl ? textEl.textContent : button.innerHTML;
    const restoreWithHtml = !textEl;
 
    button.disabled = true;
    this.setButtonLabel(button, 'ADDING...');
 
    try {
      const addedWithTheme = await this.addItemWithThemeCart(form);
      if (!addedWithTheme) {
        await this.addItemWithFetch(form);
      }
 
      this.setButtonLabel(button, 'ADDED');
 
      setTimeout(() => {
        this.setButtonLabel(button, previousLabel, restoreWithHtml);
        button.disabled = false;
      }, 1200);
    } catch (error) {
      this.setButtonLabel(button, previousLabel, restoreWithHtml);
      button.disabled = false;
    }
  }
 
  initAccordions() {
    this.root.querySelectorAll('[data-tbc-accordion]').forEach((accordion) => {
      const trigger = accordion.querySelector('[data-tbc-accordion-trigger]');
      const panel = accordion.querySelector('[data-tbc-accordion-panel]');
      if (!trigger || !panel) return;
 
      trigger.addEventListener('click', () => {
        const isOpen = accordion.classList.contains('is-open');
        accordion.classList.toggle('is-open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
        panel.hidden = isOpen;
      });
    });
  }
 
  initCompleteTheLook() {
    this.root.querySelectorAll('[data-tbc-ctl-item]').forEach((item) => {
      const product = this.parseJSON(item.querySelector('[data-tbc-ctl-product-json]'));
      const form = item.querySelector('[data-tbc-ctl-form]');
      const variantInput = item.querySelector('[data-tbc-ctl-variant-id]');
      const atc = item.querySelector('[data-tbc-ctl-atc]');
      const priceCurrent = item.querySelector('[data-tbc-ctl-price-current]');
      const priceCompare = item.querySelector('[data-tbc-ctl-price-compare]');
      const priceBadge = item.querySelector('[data-tbc-ctl-price-badge]');
      const colorLabel = item.querySelector('[data-tbc-ctl-color-label]');
      if (!product || !form) return;
 
      const selectedOptions = product.options.map((_, index) => {
        const selectedBtn = item.querySelector(
          `[data-tbc-ctl-option][data-option-index="${index}"] [data-tbc-ctl-option-btn].is-selected`
        );
        return selectedBtn ? selectedBtn.dataset.value : product.variants[0]?.[`option${index + 1}`];
      });
 
      const getVariant = () =>
        product.variants.find((variant) =>
          selectedOptions.every((value, index) => variant[`option${index + 1}`] === value)
        ) || null;
 
      const refresh = () => {
        const variant = getVariant();
        if (!variant) {
          if (atc) atc.disabled = true;
          return;
        }
 
        if (variantInput) variantInput.value = variant.id;
        if (atc) atc.disabled = !variant.available;
 
        if (priceCurrent) priceCurrent.textContent = this.formatMoney(variant.price);
 
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (priceCompare) {
          priceCompare.textContent = onSale ? this.formatMoney(variant.compare_at_price) : '';
          priceCompare.classList.toggle('is-hidden', !onSale);
        }
        if (priceBadge) {
          if (onSale) {
            const percent = Math.round(
              ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
            );
            priceBadge.textContent = `-${percent}%`;
            priceBadge.classList.remove('is-hidden');
          } else {
            priceBadge.classList.add('is-hidden');
          }
        }
 
        item.querySelectorAll('[data-tbc-ctl-option]').forEach((optionEl) => {
          const optionIndex = Number(optionEl.dataset.optionIndex);
          const optionName = (product.options[optionIndex] || '').toLowerCase();
 
          optionEl.querySelectorAll('[data-tbc-ctl-option-btn]').forEach((btn) => {
            const trial = [...selectedOptions];
            trial[optionIndex] = btn.dataset.value;
            const available = product.variants.some((variantItem) => {
              const matches = trial.every((value, index) => variantItem[`option${index + 1}`] === value);
              return matches && variantItem.available;
            });
            btn.classList.toggle('is-disabled', !available);
          });
 
          if (colorLabel && (optionName === 'color' || optionName === 'colour')) {
            colorLabel.textContent = selectedOptions[optionIndex];
          }
        });
      };
 
      item.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-tbc-ctl-option-btn]');
        if (!btn || !item.contains(btn) || btn.classList.contains('is-disabled')) return;
 
        const optionEl = btn.closest('[data-tbc-ctl-option]');
        const optionIndex = Number(optionEl.dataset.optionIndex);
        selectedOptions[optionIndex] = btn.dataset.value;
 
        optionEl.querySelectorAll('[data-tbc-ctl-option-btn]').forEach((node) => {
          node.classList.toggle('is-selected', node === btn);
        });
 
        refresh();
      });
 
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.addToCart(form, atc);
      });
 
      refresh();
    });
  }
}
 
function initTBCProductInfo() {
  document.querySelectorAll('[data-tbc-product-info]').forEach((root) => {
    if (root.dataset.tbcInfoInit === 'true') return;
    root.dataset.tbcInfoInit = 'true';
    new TBCProductInfo(root);
  });
}
 
document.addEventListener('DOMContentLoaded', initTBCProductInfo);
 
document.addEventListener('shopify:section:load', (event) => {
  const info = event.target.querySelector('[data-tbc-product-info]');
  if (!info) return;
  info.dataset.tbcInfoInit = 'false';
  new TBCProductInfo(info);
});